import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Shield, TrendingUp, CheckCircle2, Loader2 } from "lucide-react";

export const TrackRecordBadge = ({ days = 30 }) => {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/accuracy/track_record?days=${days}`)
      .then((res) => {
        if (mounted) {
          setRecord(res.data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, [days]);

  if (loading) {
    return (
      <div
        className="inline-flex items-center gap-2 px-4 h-9 rounded-full border border-border bg-card text-muted-foreground text-xs font-medium"
        data-testid="track-record-badge-loading"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Loading verified track record…
      </div>
    );
  }

  if (!record) return null;

  const winRate = record.win_rate ?? 0;
  const settled = record.total_settled ?? 0;
  const isPending = settled === 0;
  const isStrong = winRate >= 60;
  const isOk = winRate >= 50;

  let icon = <Shield className="w-3.5 h-3.5" />;
  let tone = "text-muted-foreground border-border bg-card";
  let valueTone = "text-foreground";

  if (isPending) {
    tone = "text-muted-foreground border-border bg-card";
  } else if (isStrong) {
    icon = <CheckCircle2 className="w-3.5 h-3.5" />;
    tone = "text-success border-success/40 bg-success/10";
    valueTone = "text-success";
  } else if (isOk) {
    icon = <TrendingUp className="w-3.5 h-3.5" />;
    tone = "text-warning border-warning/40 bg-warning/10";
    valueTone = "text-warning";
  } else {
    icon = <Shield className="w-3.5 h-3.5" />;
    tone = "text-destructive border-destructive/40 bg-destructive/10";
    valueTone = "text-destructive";
  }

  const strongLabel = isPending ? "0 settled" : `${winRate}%`;
  const subLabel = isPending
    ? `${days}-day track record building`
    : `${days}-day verified accuracy`;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`inline-flex items-center gap-2 h-9 px-3.5 rounded-full border text-xs font-semibold cursor-help transition-colors ${tone}`}
            data-testid="track-record-badge"
          >
            {icon}
            <span className={`font-tabular ${valueTone}`} data-testid="track-record-strong">
              {strongLabel}
            </span>
            <span className="h-1 w-1 rounded-full bg-current opacity-40" />
            <span className="text-foreground" data-testid="track-record-label">
              {subLabel}
            </span>
            {!isPending && (
              <span className="ml-1 font-tabular text-muted-foreground font-medium">
                · {record.won}W/{record.lost}L{record.void ? `/${record.void}V` : ""}
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-1 text-xs">
            <div className="font-semibold text-sm">Verified Track Record — {days} days</div>
            <div>{settled} predictions settled against real match results.</div>
            <div className="opacity-75">
              Updated {record.updated_at ? new Date(record.updated_at).toLocaleString() : "—"}
            </div>
            {record.by_market && Object.keys(record.by_market).length > 0 && (
              <div className="pt-1 border-t border-border mt-1">
                <div className="text-muted-foreground">By market</div>
                {Object.entries(record.by_market)
                  .sort((a, b) => b[1].settled - a[1].settled)
                  .slice(0, 5)
                  .map(([mt, s]) => (
                    <div key={mt} className="flex justify-between gap-3">
                      <span className="truncate">{mt}</span>
                      <span className="font-tabular">
                        {s.win_rate}% ({s.won}/{s.settled})
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default TrackRecordBadge;
